import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, MapPin, Clock } from 'lucide-react';
import BlogEmailCapture from '../components/BlogEmailCapture';
import MidtownStudioCallout from '../components/MidtownStudioCallout';

const BlogPostShouldITip = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Should I Tip My Massage Therapist? | Ayumi Suzuki';
    return () => { document.title = prev; };
  }, []);

  const isoDate = '2026-02-14';
  const canonical = 'https://ayumisuzuki.com/blog/should-i-tip-my-massage-therapist';

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50" data-testid="should-i-tip-blog-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: 'Should I Tip My Massage Therapist? A Sacramento Therapist\'s Honest Answer',
            description:
              'Should you tip your massage therapist? An honest, personable answer from midtown Sacramento CAMTC-certified therapist Ayumi Suzuki — what\'s customary (15–20%), when tipping isn\'t expected, and how she actually feels about it.',
            image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80',
            author: { '@type': 'Person', name: 'Ayumi Suzuki', url: 'https://ayumisuzuki.com/about', jobTitle: 'Certified Massage Therapist' },
            publisher: {
              '@type': 'Organization',
              name: 'Ayumi Suzuki Massage Therapy',
              logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/hdysr3f5_ayumi1%20logo.jpg' },
            },
            datePublished: isoDate,
            dateModified: isoDate,
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
            articleSection: 'Personal Notes',
            keywords: 'should I tip my massage therapist, massage tipping etiquette, how much to tip massage therapist, tipping massage Sacramento, Ayumi Suzuki',
            about: [{ '@type': 'Thing', name: 'Massage Tipping Etiquette' }, { '@type': 'Place', name: 'Sacramento, California' }],
            inLanguage: 'en-US',
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'QAPage',
            mainEntity: {
              '@type': 'Question',
              name: 'Should I tip my massage therapist?',
              text: 'Should I tip my massage therapist, and if so, how much is standard?',
              answerCount: 1,
              author: { '@type': 'Person', name: 'Ayumi Suzuki' },
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'In the United States, tipping a massage therapist is customary at a spa or private practice. The standard is 15–20% of the pre-tax service price, similar to tipping at a restaurant or salon. A 60-minute massage at $110 would mean a tip of about $17–$22. Tipping is not expected at medical/clinical massage settings (physical therapy offices, chiropractic clinics, hospitals). If you can\'t tip or forget to, don\'t stress — showing up, communicating openly, and re-booking are equally meaningful to your therapist. At Ayumi Suzuki\'s midtown Sacramento practice, tips are appreciated but never expected.',
                author: { '@type': 'Person', name: 'Ayumi Suzuki', jobTitle: 'Certified Massage Therapist' },
                dateCreated: isoDate,
                upvoteCount: 0,
                url: canonical,
              },
            },
          }),
        }}
      />

      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/blog" className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group mb-4" data-testid="back-to-blog-link">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Blog</span>
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="flex items-center" data-testid="post-date"><Calendar className="w-4 h-4 mr-2" /><time dateTime={isoDate}>February 14, 2026</time></span>
            <span className="flex items-center"><User className="w-4 h-4 mr-2" />Ayumi Suzuki</span>
            <span className="flex items-center"><MapPin className="w-4 h-4 mr-2" />Midtown Sacramento</span>
            <span className="flex items-center"><Clock className="w-4 h-4 mr-2" />6 min read</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6 leading-tight" data-testid="post-title">
            Should I Tip My Massage Therapist?
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            One of the quiet questions almost every client thinks about — and almost none of them ask out loud. Here's my honest, no-pressure answer.
          </p>
        </header>

        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80" alt="A calm, welcoming reception space at a midtown Sacramento massage studio" className="w-full h-96 object-cover" />
        </div>

        <div className="bg-white border border-teal-100 rounded-2xl p-6 mb-12 shadow-sm">
          <h2 className="text-xl font-serif text-teal-700 mb-2">The Short Answer</h2>
          <p className="text-gray-700 leading-relaxed">
            Yes — at a spa or private practice like mine, <strong>15–20% of the pre-tax service price</strong> is customary in the U.S. On a $110 sixty-minute session, that's roughly <strong>$17–$22</strong>. Tipping isn't expected in medical/clinical settings, and if you can't tip on a given visit, please just come anyway. Nothing about your session changes.
          </p>
        </div>

        <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-a:text-teal-700">
          <h2>How I Actually Feel About Tipping</h2>
          <p>
            Honestly? I don't want you to think about it for more than a few seconds. If you're the kind of person who tips 20% at a restaurant, tip me 20%. If money's tight this month, come anyway. I'd rather see you every four weeks with no tip than see you every six months with a big one.
          </p>
          <p>
            The reason tipping exists in this industry is the same reason it exists in restaurants and salons: it's how a lot of therapists make a livable wage, especially at spas where the therapist keeps only a fraction of the sticker price. In a private practice like mine, it matters less — but it's still appreciated, and it's still customary.
          </p>

          <h2>What's Actually Customary</h2>
          <p>
            Here's the standard I hand to clients when they ask, without making it weird:
          </p>
          <ul>
            <li><strong>15%</strong> — good service, nothing to complain about.</li>
            <li><strong>20%</strong> — you're happy and want to come back.</li>
            <li><strong>25%+</strong> — the session did something for you and you want to say so.</li>
          </ul>
          <p>
            All of that is calculated on the <em>pre-tax</em> service price, not the total charge with any tax or added fees.
          </p>

          <h2>Quick Math for My Sacramento Rates</h2>
          <p>
            Because "figure out 18% in my head" is not what anyone wants to do while feeling floaty on the way out:
          </p>
          <ul>
            <li><strong>30-min · $55</strong> → 15% ≈ $8 · 20% ≈ $11</li>
            <li><strong>45-min · $70</strong> → 15% ≈ $11 · 20% ≈ $14</li>
            <li><strong>60-min · $110</strong> → 15% ≈ $17 · 20% ≈ $22</li>
            <li><strong>90-min · $160</strong> → 15% ≈ $24 · 20% ≈ $32</li>
            <li><strong>120-min · $200</strong> → 15% ≈ $30 · 20% ≈ $40</li>
          </ul>

          <h2>When You Don't Have to Tip</h2>
          <p>
            There are settings where tipping isn't customary and, honestly, sometimes not even allowed:
          </p>
          <ul>
            <li><strong>Physical therapy or chiropractic offices</strong> — massage as medical treatment, often billed to insurance.</li>
            <li><strong>Hospitals and integrative medicine clinics.</strong></li>
            <li><strong>When your session was covered by an HSA/FSA or health insurance</strong> as a medical service.</li>
          </ul>
          <p>
            If you're ever unsure, ask the front desk (or ask me at intake). No one will be offended by the question.
          </p>

          <h2>Cash, Card, or Venmo?</h2>
          <p>
            All three work. At my studio you can add a tip to your card when you check out, hand me cash on the way out, or send it after the fact via Venmo or Zelle. Whatever's easiest for you. Nothing weird happens either way — I still text you a thank-you.
          </p>

          <h2>What If You Forgot?</h2>
          <p>
            Deep breath — it's fine. Really. If it's bothering you, add it to your next session, send it later, or forget it entirely. I mean this: I'd never want anyone to skip a session they need because they're worried about the tip.
          </p>

          <h2>A Few Other Honest Answers</h2>

          <h3>Do you tip the owner?</h3>
          <p>Some clients believe you don't tip a business owner. That's a decades-old etiquette rule from an era before independent practitioners. In a modern private practice, tips are welcomed and normal. Follow your instinct.</p>

          <h3>Is it rude to not tip?</h3>
          <p>Not to me. I don't keep a mental list. Please don't let this be a reason to not come in.</p>

          <h3>What's the best thing you can do besides tipping?</h3>
          <p>Book again. And, if you liked your session, a Google review means more than a tip to almost any small massage practice.</p>

          <h3>Do I tip more for deep tissue?</h3>
          <p>No. Tip is a percentage of the service price, and deep tissue is priced the same as Swedish here. Some places charge a premium — check the menu.</p>

          <h3>Do I tip for the price of a gift card or the price of the session?</h3>
          <p>Tip on the pre-tax service value received. Not on the gift card face value.</p>

          <h2>The Point I Really Want You to Leave With</h2>
          <p>
            Tipping is a way to say thank you. So is showing up regularly. So is telling a friend. So is being an easy, communicative client on the table. All of it counts. The best thing you can do for your therapist is keep taking care of yourself.
          </p>

          {/* CTA */}
          <div className="not-prose bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white mt-12">
            <h3 className="text-2xl font-serif mb-4">Come In When You're Ready</h3>
            <p className="mb-6">Midtown Sacramento, 2528 I Street. Tip or no tip, I'm glad you're here. Book online any time or text me at <a href="sms:+14156789133" className="underline">415-678-9133</a>.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book" className="inline-block bg-white text-teal-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-300 text-center" data-testid="cta-book-now">Book a Session</Link>
              <Link to="/services-menu" className="inline-block bg-teal-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-900 transition-colors duration-300 text-center" data-testid="cta-services">See Services & Pricing</Link>
            </div>
          </div>
        </div>

        <MidtownStudioCallout />
        <BlogEmailCapture source="should-i-tip" />

        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-start space-x-6">
            <img src="https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/aautielc_Ayumi.jpg" alt="Ayumi Suzuki" className="w-24 h-24 rounded-full object-cover" />
            <div>
              <h3 className="text-2xl font-serif text-gray-800 mb-2">— Ayumi</h3>
              <p className="text-gray-600 mb-4">
                California CAMTC-certified. 2528 I Street, midtown Sacramento. If you have questions before booking, please text me — no such thing as an awkward one.
              </p>
              <Link to="/about" className="text-teal-600 hover:text-teal-700 font-medium">Read my longer story →</Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostShouldITip;
